<template>
  <RuesBoxSize
    v-if="reviews.length !== 0"
    :size="props.item?.width"
    class="text-left grid grid-cols-1 gap-x-4 gap-y-4 product-reviews-carousel"
  >
    <UiHeadline
      v-if="props.item?.headline != ''"
      type="h2"
      design="h2"
    >
      {{ props.item?.headline }}
    </UiHeadline>
    <UiHeadline
      v-else
      type="h2"
      design="h2"
    >
      <UiLanguagePlaceholder
        domain="RuesProductReviews"
        name="headline"
        :variables="{ region: props.item?.region, preposition: props.item?.preposition }"
      />
    </UiHeadline>

    <div
      v-if="!showSkeleton"
      class="relative"
    >
      <UiCarousel
        class="embla-reviews"
        :slide-size="SLIDE_SIZE_MOBILE"
        :slide-count="total"
        :ssr-breakpoints="ssrBreakpoints"
        :show-arrows="total > 1"
        :options="carouselOptions"
        :accessibility="accessibilityOptions"
      >
        <div
          v-for="review in reviews"
          :key="review.id"
          class="embla__slide px-2 py-3"
        >
          <SharedObjectCardWithReviews :review="review" />
        </div>

        <template
          v-if="total > 1"
          #prev
        >
          <span aria-hidden="true" />
        </template>
        <template
          v-if="total > 1"
          #next
        >
          <span aria-hidden="true" />
        </template>
      </UiCarousel>
    </div>

    <div
      v-show="showSkeleton"
      class="flex flex-nowrap gap-x-4 w-full overflow-hidden py-3"
    >
      <SkeletonSharedObjectCardWithReviews class="w-[92%] md:w-[41.7%] lg:w-[33.3333%]" />
      <SkeletonSharedObjectCardWithReviews class="md:w-[41.7%] lg:w-[33.3333%] hidden md:block" />
      <SkeletonSharedObjectCardWithReviews class="lg:w-[33.3333%] hidden lg:block" />
      <SkeletonSharedObjectCardWithReviews
        :is-half="true"
        class="w-[8%] md:w-[12.2%] block lg:hidden"
      />
    </div>
  </RuesBoxSize>
</template>

<script setup lang="ts">
import type { EmblaOptionsType } from 'embla-carousel'
import type { AccessibilityOptionsType } from 'embla-carousel-accessibility'
import type { SsrOptionsType } from 'embla-carousel-ssr'
import type { ObjektTeaserMitBewertungElementData } from '~/types/TypesFromPortalBackend'

const SLIDE_SIZE_MOBILE = 90.9
const SLIDE_SIZE_TABLET = 47.6
const SLIDE_SIZE_DESKTOP = 33.333

const { translate: t } = useTranslations('RuesProductReviews')

interface Props {
  item?: ObjektTeaserMitBewertungElementData | null
  skeleton?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  item: null,
  skeleton: true
})

const loaded = ref(false)
const showSkeleton = computed(() => props.skeleton && !loaded.value)

const reviews = computed(() => {
  const raw = props.item?.reviews
  if (Array.isArray(raw)) {
    return raw
  }
  if (raw && typeof raw === 'object') {
    return Object.values(raw)
  }
  return []
})

const total = computed(() => reviews.value.length)

const ssrBreakpoints = computed(() => {
  const n = Math.max(total.value, 0)
  return {
    '(min-width: 768px)': { slideSizes: Array.from({ length: n }, () => SLIDE_SIZE_TABLET) },
    '(min-width: 1024px)': { slideSizes: Array.from({ length: n }, () => SLIDE_SIZE_DESKTOP) }
  } as NonNullable<SsrOptionsType['breakpoints']>
})

const carouselOptions = computed<EmblaOptionsType>(() => ({
  align: 'start',
  loop: false
}))

const accessibilityOptions = computed<AccessibilityOptionsType>(() => ({
  announceChanges: true,
  carouselAriaLabel: props.item?.headline || t.value('carousel_aria_label'),
  previousButtonAriaLabel: t.value('previous_button_aria_label'),
  nextButtonAriaLabel: t.value('next_button_aria_label'),
  slideAriaLabel: (_grouped, first) => t.value('slide_aria_label', { current: first + 1, total: total.value }),
  liveRegionContent: (_grouped, first) => t.value('live_region_content', { current: first + 1, total: total.value }),
  rootNode: emblaRoot => emblaRoot.parentElement
}))

onMounted(() => {
  loaded.value = true
})
</script>

<style src="./css/RuesProductReviews.css" scoped></style>
